// Koordinations-Zeiträume eines Trainers im Geld- & Rechnungsfluss.
//
// Standard ist dauerhafte Koordination: ein Eintrag mit leerem valid_from/valid_to.
// Bei Bedarf kann die Koordination zeitlich eingeschränkt werden (mehrere Zeiträume möglich).
//
// Datumsgrenzen (valid_from / valid_to) sind inklusive Vereins-Kalendertage.
// „Heute“ = Kalendertag in der Vereinszeitzone (Fallback Europe/Berlin), nie Browser-Lokalzeit oder UTC-Slice.
//
// Periodenauflösung: bei mehreren Treffern am selben Tag gewinnt der erste Eintrag
// in parse-Reihenfolge (nach Save nach valid_from sortiert). Overlap-Validierung: späteres Patch.
// TODO(coordination-overlap): Mehrfachtreffer am Stichtag diagnostizieren / Schreibschutz.

import {
  resolveTenantCalendarTimeZone,
  resolveTenantCalendarYmd,
} from "../location/calendar-ymd.js";

export {
  resolveTenantCalendarTimeZone,
  resolveTenantCalendarYmd,
} from "../location/calendar-ymd.js";

function nz(v) {
  return String(v ?? "").trim();
}

function parseYmd(value) {
  const s = nz(value).slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return "";
  return s;
}

function ymdCompare(a, b) {
  return String(a).localeCompare(String(b));
}

function asPeriodList(periodsOrTrainer) {
  if (Array.isArray(periodsOrTrainer)) {
    return periodsOrTrainer
      .map((p) => ({
        coordinator_trainer_id: nz(
          p?.coordinator_trainer_id ?? p?.coordinatorTrainerId,
        ),
        valid_from: parseYmd(p?.valid_from),
        valid_to: parseYmd(p?.valid_to),
      }))
      .filter((p) => p && typeof p === "object");
  }
  return parseTrainerCoordinationPeriods(periodsOrTrainer);
}

/**
 * Fachlicher Vereins-Kalendertag für Koordination („heute“).
 * @param {Date|{ now?: Date|number|string, timeZone?: string, timezone?: string }|null|undefined} [nowOrOpts]
 * @returns {string} YYYY-MM-DD
 */
export function coordinationTodayYmd(nowOrOpts = new Date()) {
  if (
    nowOrOpts instanceof Date ||
    typeof nowOrOpts === "number" ||
    nowOrOpts == null
  ) {
    return resolveTenantCalendarYmd({ now: nowOrOpts ?? new Date() });
  }
  if (typeof nowOrOpts === "object") {
    return resolveTenantCalendarYmd(nowOrOpts);
  }
  return resolveTenantCalendarYmd({ now: new Date() });
}

/**
 * Addiert Kalendertage zu einem YYYY-MM-DD (reine Kalenderarithmetik, UTC-Mittagsanker).
 * @param {string} ymd
 * @param {number} deltaDays
 * @returns {string}
 */
export function coordinationAddDaysYmd(ymd, deltaDays) {
  const base = parseYmd(ymd) || coordinationTodayYmd();
  const [y, m, d] = base.split("-").map((n) => Number(n));
  if (![y, m, d].every((n) => Number.isFinite(n))) return "";
  const dt = new Date(Date.UTC(y, m - 1, d + Number(deltaDays || 0), 12, 0, 0));
  if (!Number.isFinite(dt.getTime())) return "";
  const yy = dt.getUTCFullYear();
  const mm = String(dt.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(dt.getUTCDate()).padStart(2, "0");
  return `${yy}-${mm}-${dd}`;
}

/**
 * Enddatum für sofortiges Pausieren: gestern, damit heute nicht mehr gilt
 * (valid_to ist inklusiv). Vereins-Kalendertag.
 * @param {Date|{ now?: Date|number|string, timeZone?: string }|null|undefined} [nowOrOpts]
 */
export function coordinationPauseEndYmd(nowOrOpts = new Date()) {
  return coordinationAddDaysYmd(coordinationTodayYmd(nowOrOpts), -1);
}

/**
 * Liest die Koordinations-Zeiträume eines Trainers aus.
 *
 * @param {object|null|undefined} trainer
 * @returns {Array<{ coordinator_trainer_id: string, valid_from: string, valid_to: string }>}
 */
export function parseTrainerCoordinationPeriods(trainer) {
  const raw = trainer?.coordination_periods ?? trainer?.coordinationPeriods;
  if (!Array.isArray(raw)) return [];
  return raw
    .map((p) => ({
      coordinator_trainer_id: nz(
        p?.coordinator_trainer_id ?? p?.coordinatorTrainerId,
      ),
      valid_from: parseYmd(p?.valid_from),
      valid_to: parseYmd(p?.valid_to),
    }))
    .filter((p) => p && typeof p === "object");
}

/**
 * Normalisiert Zeiträume für die Persistenz (verwirft komplett leere Einträge,
 * trimmt Daten). Leeres valid_from/valid_to bleibt erlaubt (= unbefristet).
 *
 * @param {Array<{ coordinator_trainer_id?: string, valid_from?: string, valid_to?: string }>|null|undefined} periods
 */
export function serializeTrainerCoordinationPeriodsForSave(periods) {
  const list = Array.isArray(periods) ? periods : [];
  return list
    .map((p) => ({
      coordinator_trainer_id: nz(
        p?.coordinator_trainer_id ?? p?.coordinatorTrainerId,
      ),
      valid_from: parseYmd(p?.valid_from),
      valid_to: parseYmd(p?.valid_to),
    }))
    .sort((a, b) => ymdCompare(a.valid_from || "0000", b.valid_from || "0000"));
}

/**
 * Gibt an, ob für den Trainer überhaupt eine Koordination hinterlegt ist
 * (inkl. bereits beendeter Zeiträume).
 *
 * @param {object|null|undefined} trainer
 */
export function isTrainerCoordinationEnabled(trainer) {
  return parseTrainerCoordinationPeriods(trainer).length > 0;
}

/**
 * Inklusiv: valid_from ≤ ymd ≤ valid_to (leere Grenzen = unbefristet).
 * @param {{ valid_from?: string, valid_to?: string }|null|undefined} period
 * @param {string} ymd
 */
function periodCoversDate(period, ymd) {
  const vf = parseYmd(period?.valid_from);
  const vt = parseYmd(period?.valid_to);
  if (vf && ymdCompare(ymd, vf) < 0) return false;
  if (vt && ymdCompare(ymd, vt) > 0) return false;
  return true;
}

/**
 * Liefert den Koordinations-Zeitraum, der ein Datum abdeckt (oder null).
 * Bei mehreren Treffern: erster Treffer in Periodenreihenfolge (siehe Modulkommentar).
 *
 * @param {object|null|undefined} trainer
 * @param {string} date
 */
export function resolveTrainerCoordinationPeriodForDate(trainer, date) {
  const ymd = parseYmd(date);
  if (!ymd) return null;
  for (const p of parseTrainerCoordinationPeriods(trainer)) {
    if (periodCoversDate(p, ymd)) return p;
  }
  return null;
}

/** Alias: fachliche API-Bezeichnung für Periodenauflösung am Stichtag. */
export function resolveTrainerCoordinationForDate(trainer, date) {
  return resolveTrainerCoordinationPeriodForDate(trainer, date);
}

/**
 * Ist der Trainer am angegebenen Datum koordiniert (Geld- & Rechnungsfluss über Koordinator)?
 * Grenzen inklusiv (Vereins-Kalendertag).
 *
 * @param {object|null|undefined} trainer
 * @param {string} date
 * @returns {boolean}
 */
export function isTrainerCoordinatedAtDate(trainer, date) {
  return resolveTrainerCoordinationPeriodForDate(trainer, date) != null;
}

/** Alias: fachliche API-Bezeichnung für aktive Periodenprüfung. */
export function isCoordinationPeriodActiveOnDate(trainer, date) {
  return isTrainerCoordinatedAtDate(trainer, date);
}

/**
 * Koordinator-Trainer-ID, die den Trainer am Datum koordiniert.
 * Leerer String bedeutet: koordiniert durch den Tenant-Koordinator ohne expliziten Trainer-Bezug.
 *
 * @param {object|null|undefined} trainer
 * @param {string} date
 * @returns {string|null} null = nicht koordiniert
 */
export function resolveTrainerCoordinatorForDate(trainer, date) {
  const period = resolveTrainerCoordinationPeriodForDate(trainer, date);
  if (!period) return null;
  return nz(period.coordinator_trainer_id);
}

/** Standard-Eintrag für eine dauerhafte Koordination. */
export function buildPermanentCoordinationPeriod(coordinatorTrainerId = "") {
  return {
    coordinator_trainer_id: nz(coordinatorTrainerId),
    valid_from: "",
    valid_to: "",
  };
}

/**
 * Beendet laufende/offene Koordinationszeiträume zum Enddatum.
 * Bereits früher beendete und rein zukünftige Zeiträume bleiben unverändert.
 * Historie wird nicht gelöscht.
 *
 * @param {object|Array|null|undefined} periodsOrTrainer
 * @param {string} endDate YYYY-MM-DD
 * @param {{ coordinatorTrainerId?: string }} [options]
 */
export function endTrainerCoordinationPeriodsAtDate(
  periodsOrTrainer,
  endDate,
  options = {},
) {
  const end = parseYmd(endDate);
  const list = asPeriodList(periodsOrTrainer);
  if (!end) return list;
  const fallbackCoordinatorId = nz(options.coordinatorTrainerId);

  return list.map((p) => {
    const vf = parseYmd(p.valid_from);
    const vt = parseYmd(p.valid_to);
    const coordinator_trainer_id =
      nz(p.coordinator_trainer_id) || fallbackCoordinatorId;
    if (vf && ymdCompare(vf, end) > 0) {
      return { ...p, coordinator_trainer_id };
    }
    if (vt && ymdCompare(vt, end) <= 0) {
      return { ...p, coordinator_trainer_id };
    }
    return {
      coordinator_trainer_id,
      valid_from: vf,
      valid_to: end,
    };
  });
}

/**
 * Startet (erneut) eine Koordination ab einem Datum, ohne Historie zu löschen.
 *
 * @param {object|Array|null|undefined} periodsOrTrainer
 * @param {{ coordinatorTrainerId?: string, validFrom?: string }} [options]
 */
export function startTrainerCoordinationPeriods(
  periodsOrTrainer,
  options = {},
) {
  const list = asPeriodList(periodsOrTrainer);
  const coordinatorTrainerId = nz(options.coordinatorTrainerId);
  const validFrom = parseYmd(options.validFrom);
  if (!list.length && !validFrom) {
    return [buildPermanentCoordinationPeriod(coordinatorTrainerId)];
  }
  return [
    ...list,
    {
      coordinator_trainer_id: coordinatorTrainerId,
      valid_from: validFrom,
      valid_to: "",
    },
  ];
}

/**
 * UI-Status der Koordination zum Stichtag.
 *
 * @param {object|null|undefined} trainer
 * @param {string} [asOfYmd]
 * @returns {{ kind: 'none'|'permanent'|'activeUntil'|'ended', validTo?: string }}
 */
export function resolveTrainerCoordinationUiStatus(trainer, asOfYmd) {
  const asOf = parseYmd(asOfYmd) || coordinationTodayYmd();
  const periods = parseTrainerCoordinationPeriods(trainer);
  if (!periods.length) return { kind: "none" };

  if (isTrainerCoordinatedAtDate(trainer, asOf)) {
    const period = resolveTrainerCoordinationPeriodForDate(trainer, asOf);
    const vt = parseYmd(period?.valid_to);
    if (vt) return { kind: "activeUntil", validTo: vt };
    return { kind: "permanent" };
  }

  const latestEnd = periods
    .map((p) => parseYmd(p.valid_to))
    .filter(Boolean)
    .sort((a, b) => ymdCompare(b, a))[0];
  if (latestEnd) return { kind: "ended", validTo: latestEnd };
  return { kind: "none" };
}

/**
 * Frühester Beginn der aktuell gültigen Koordination (oder leer bei unbefristet ohne From).
 *
 * @param {object|null|undefined} trainer
 * @param {string} [asOfYmd]
 * @returns {string} YYYY-MM-DD oder ""
 */
export function resolveActiveCoordinationStartYmd(trainer, asOfYmd) {
  const asOf = parseYmd(asOfYmd) || coordinationTodayYmd();
  const period = resolveTrainerCoordinationPeriodForDate(trainer, asOf);
  return parseYmd(period?.valid_from);
}

/**
 * Trainer, die der angegebene Koordinator am Stichtag betreut.
 * Leere coordinator_trainer_id gilt als „Tenant-Koordinator ohne expliziten Bezug“
 * und wird nur mitgezählt, wenn `includeUnassigned` true ist.
 *
 * @param {object[]} trainers
 * @param {string} coordinatorTrainerId
 * @param {{ asOfYmd?: string, includeUnassigned?: boolean }} [opts]
 * @returns {object[]}
 */
export function listTrainersCoordinatedBy(
  trainers,
  coordinatorTrainerId,
  opts = {},
) {
  const coordinatorId = nz(coordinatorTrainerId);
  const asOf = parseYmd(opts.asOfYmd) || coordinationTodayYmd();
  const includeUnassigned = Boolean(opts.includeUnassigned);
  const rows = Array.isArray(trainers) ? trainers : [];
  if (!coordinatorId && !includeUnassigned) return [];

  return rows.filter((tr) => {
    const cid = resolveTrainerCoordinatorForDate(tr, asOf);
    if (cid == null) return false;
    if (cid === coordinatorId) return true;
    return includeUnassigned && cid === "" && Boolean(coordinatorId);
  });
}

/**
 * Trainer mit Koordinations-Bezug zu diesem Koordinator (auch pausiert/beendet).
 * Damit bleiben pausierte Trainer in „Meine Trainer“ mit Fortsetzen sichtbar.
 *
 * @param {object[]} trainers
 * @param {string} coordinatorTrainerId
 * @returns {object[]}
 */
export function listTrainersAssociatedWithCoordinator(
  trainers,
  coordinatorTrainerId,
) {
  const coordinatorId = nz(coordinatorTrainerId);
  if (!coordinatorId) return [];
  const rows = Array.isArray(trainers) ? trainers : [];
  return rows.filter((tr) =>
    parseTrainerCoordinationPeriods(tr).some(
      (p) => nz(p.coordinator_trainer_id) === coordinatorId,
    ),
  );
}

/**
 * Soll der Nav-Punkt „Meine Trainer“ sichtbar sein?
 *
 * @param {{
 *   mode?: string,
 *   viewerTrainerId?: string,
 *   trainers?: object[],
 *   asOfYmd?: string,
 * }} input
 */
export function canShowMyTrainersNav({
  mode,
  viewerTrainerId,
  trainers = [],
  asOfYmd,
} = {}) {
  const viewerMode = String(mode || "").trim();
  const isCoordinatorRole =
    viewerMode === "coordinator" || viewerMode === "planner";
  const tid = nz(viewerTrainerId);
  const associated = tid
    ? listTrainersAssociatedWithCoordinator(trainers, tid).length > 0
    : false;
  const coordinated = tid
    ? listTrainersCoordinatedBy(trainers, tid, { asOfYmd }).length > 0
    : false;
  return isCoordinatorRole || associated || coordinated;
}
